import { formatDate } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import {
  InfoSection,
  type InfoSectionProps,
} from "@src/appV2/redesign/ClipboardScore/components/Page/Info";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { Divider, PageHeaderWrapper, Pill, Text } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { Fragment } from "react";

function getSections(liftDate: string | undefined): InfoSectionProps[] {
  const renderedLiftDate = isDefined(liftDate) ? <b>{liftDate}</b> : "your original lift date";

  return [
    {
      iconType: "unlock",
      title: "How it works",
      description: (
        <Text variant="body2">
          You can now claim same-day shifts. They&apos;ll show up in your feed just like normal.
          We&apos;ve reset your score to <b>80</b> so you can start booking, and points you earn
          carry over once your restriction lifts.
        </Text>
      ),
    },
    {
      iconType: "triangle-exclamation",
      title: "If your score drops to 0",
      description: (
        <Text variant="body2">
          Your restriction will resume and you won&apos;t be able to claim same-day shifts. Note,
          your restriction doesn&apos;t reset — it will still lift on {renderedLiftDate}.
        </Text>
      ),
    },
    {
      iconType: "hourglass",
      title: "Getting full access back",
      description: (
        <Text variant="body2">
          You&apos;ll have access to same-day shifts until your restriction fully lifts on{" "}
          {renderedLiftDate}. After that, you&apos;re back with no limits.
        </Text>
      ),
    },
  ];
}

export function LimitedAccessInfoPage() {
  const { userId, tmz } = useDefinedWorker();
  const { data: scoreProfile } = useGetClipboardScoreProfile(userId);

  useLogEffect(APP_V2_USER_EVENTS.LIMITED_ACCESS_INFO_PAGE_VIEWED);

  const reEnrollmentDate = scoreProfile?.accountStatus?.reEnrollmentDate;
  const liftDate = isDefined(reEnrollmentDate)
    ? formatDate(reEnrollmentDate, { timeZone: tmz })
    : undefined;
  const currentScore = scoreProfile?.clipboardScore;
  const sections = getSections(liftDate);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <Box
          sx={(theme) => ({
            background: theme.palette.intent?.warning?.gradient ?? "",
          })}
        >
          <PageHeaderWrapper sx={{ background: "transparent" }}>
            <PageHeaderTopRow />
          </PageHeaderWrapper>

          <Stack
            spacing={4}
            alignItems="center"
            sx={{
              paddingX: 6,
              paddingTop: 4,
              paddingBottom: 10,
              textAlign: "center",
            }}
          >
            <Text variant="h3">You can now claim same-day shifts</Text>
            {isDefined(currentScore) && (
              <Pill
                variant="filled"
                size="medium"
                backgroundColor={(theme) => theme.palette.background.paper}
                label={
                  <>
                    Clipboard score:{" "}
                    <Text inline semibold variant="body2">
                      {currentScore}
                    </Text>
                  </>
                }
              />
            )}
          </Stack>
        </Box>

        <Stack spacing={8} sx={{ paddingX: 7, paddingY: 8 }}>
          {sections.map((section, index) => (
            <Fragment key={section.title}>
              <InfoSection {...section} />
              {index < sections.length - 1 && <Divider />}
            </Fragment>
          ))}

          <Box sx={{ textAlign: "center", paddingY: 4 }}>
            <TextButton
              onClick={() => {
                void openZendeskMessaging();
              }}
            >
              Have questions about Limited Access? Reach out.
            </TextButton>
          </Box>
        </Stack>
      </PageWrapper>
    </ThemeProvider>
  );
}
